import { log, isNone, useStorage } from '@crocodille/common';

type AddShippingInfoEvent = {
  event: 'add_shipping_info';
  ecommerce: {
    shipping_tier: string;
  };
};

type ViewItemListEvent = {
  event: 'view_item_list';
  ecommerce: {
    items: {
      /**
       * Product name.
       */
      item_name: string;
      /**
       * SKU or ID of the item.
       */
      item_id: string;
      price: string;
      item_category: string;
      item_list_name: string;
      index: number;
    }[];
  };
};

type ViewItemEvent = {
  event: 'view_item';
  ecommerce: {
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_category: string;
      item_list_name: string;
    }[];
  };
};

type RemarketingViewItemEvent = {
  event: 'remarketing_view_item';
  value: number;
  items: {
    id: string;
    google_business_vertical: 'retail';
    quantity: number;
  }[];
};

type AddToCartEvent = {
  event: 'add_to_cart';
  ecommerce: {
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_category: string;
      item_list_name: string;
      quantity: string;
      index: number;
    }[];
  };
};

type RemarketingAddToCartEvent = {
  event: 'remarketing_add_to_cart';
  value: number;
  items: {
    id: string;
    google_business_vertical: 'retail';
    quantity: number;
  }[];
};

type RemoveFromCartEvent = {
  event: 'remove_from_cart';
  ecommerce: {
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_category: string;
      item_list_name: string;
      quantity: string;
      index: number;
    }[];
  };
};

type RemarketingRemoveFromCartEvent = {
  event: 'remarketing_remove_from_cart';
  value: number;
  items: {
    id: string;
    google_business_vertical: 'retail';
    quantity: number;
  }[];
};

type BeginCheckoutEvent = {
  event: 'begin_checkout';
  ecommerce: {
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_category: string;
      quantity: string;
    }[];
  };
};

type AddPaymentInfoEvent = {
  event: 'add_payment_info';
  ecommerce: {
    payment_type: string;
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_category: string;
      quantity: string;
    }[];
  };
};

type PurchaseEvent = {
  event: 'purchase';
  ecommerce: {
    currency: string;
    currencyCode: string;
    value: number;
    tax: number;
    shipping: number;
    transaction_id: string;
    coupon?: string;
    items: {
      item_name: string;
      item_id: string;
      price: string;
      item_brand?: string;
      item_category?: string;
      item_category2?: string;
      item_category3?: string;
      quantity: string;
    }[];
  };
};

type RemarketingPurchaseEvent = {
  event: 'remarketing_purchase';
  value: number;
  items: {
    id: string;
    google_business_vertical: 'retail';
    quantity: number;
  }[];
};

type SignUpEvent = {
  event: 'sign_up';
};

type LoginEvent = {
  event: 'login';
  user_id: string;
};

type UserInfoEvent = {
  event: 'user_info';
  lang: 'cs_CZ' | 'en_US';
  userId?: string;
  userType: 'anonymous' | 'signed_in';
};

type AnalyticsEvent =
  | AddShippingInfoEvent
  | ViewItemListEvent
  | ViewItemEvent
  | RemarketingViewItemEvent
  | AddToCartEvent
  | RemarketingAddToCartEvent
  | RemoveFromCartEvent
  | RemarketingRemoveFromCartEvent
  | BeginCheckoutEvent
  | AddPaymentInfoEvent
  | PurchaseEvent
  | RemarketingPurchaseEvent
  | SignUpEvent
  | LoginEvent
  | UserInfoEvent;

export function analyticsEvent<E extends AnalyticsEvent['event']>(
  event: E,
  data: Omit<Extract<AnalyticsEvent, { event: E }>, 'event'>,
): void {
  if (isNone(window.dataLayer)) {
    (window as { dataLayer?: unknown[] }).dataLayer = [];
  }

  if ('ecommerce' in data) {
    window.dataLayer?.push?.({
      ecommerce: null,
    });
  }

  const eventPayload = {
    event,
    ...data,
  };

  log('debug', 'analyticsEvent', eventPayload);

  window.dataLayer?.push?.(eventPayload);
}

const SEZNAM_CONVERSION_MAX_ATTEMPTS = 20;
const SEZNAM_CONVERSION_RETRY_DELAY_MS = 250;

export function fireSeznamConversion(
  conversionId: number,
  value: number,
  attempt = 0,
) {
  if (typeof window === 'undefined') {
    return;
  }

  if (isNone(window.rc)) {
    if (attempt < SEZNAM_CONVERSION_MAX_ATTEMPTS) {
      setTimeout(
        () => fireSeznamConversion(conversionId, value, attempt + 1),
        SEZNAM_CONVERSION_RETRY_DELAY_MS,
      );
    }

    return;
  }

  window.sznIVA?.IS?.updateIdentities({ eid: null });

  window.rc.conversionHit({
    id: conversionId,
    value,
    consent: null,
  });
}

export function gtag(...params: unknown[]) {
  if (typeof window === 'undefined' || isNone(window.gtag)) {
    return;
  }

  window.gtag(...params);
}

export interface Consent {
  readonly adStorage: boolean;
  readonly analyticsStorage: boolean;
  readonly functionalStorage: boolean;
}

export const DEFAULT_CONSENT: Consent = {
  adStorage: false,
  analyticsStorage: false,
  functionalStorage: false,
};

export function formatConsent(consent: Consent) {
  return {
    ad_storage: consent.adStorage ? 'granted' : 'denied',
    analytics_storage: consent.analyticsStorage ? 'granted' : 'denied',
    functional_storage: consent.functionalStorage ? 'granted' : 'denied',
  } as const;
}

export function updateConsent(consent: Consent) {
  gtag('consent', 'update', formatConsent(consent));
}

const COOKIE_CONSENT_SESSION_KEY = 'cookie_consent';

export function useConsent() {
  return useStorage<Consent>(COOKIE_CONSENT_SESSION_KEY);
}
